<div class="diff-wrapper">

  <!-- ===== HEADER ===== -->
  <div class="diff-header">
    <div class="diff-header-left">
      <h2>Gamedata Diff Viewer V2</h2>
      <span class="diff-subtitle">Compare and analyze game data changes</span>
    </div>
    <div class="diff-header-actions" *ngIf="!isLoading && selectedDiff">
      <button class="btn-action btn-sqlite" (click)="exportToSQLite()" title="Download SQLite database file">
        <span class="btn-icon">🗄️</span> Download SQLite
      </button>
      <button class="btn-action btn-csv" (click)="exportToCSV()" title="Download CSV export">
        <span class="btn-icon">📥</span> Download CSV
      </button>
    </div>
  </div>

  <!-- ===== LOADING ===== -->
  <div *ngIf="isLoading" class="state-block loading-state">
    <div class="state-spinner">🔄</div>
    <p>Loading gamedata diff…</p>
  </div>

  <!-- ===== ERROR ===== -->
  <div *ngIf="!isLoading && !selectedDiff" class="state-block error-state">
    <div class="state-icon">❌</div>
    <p *ngIf="params">No gamedata diff found for ID: <strong>{{params}}</strong></p>
    <p *ngIf="buildVersionParam">No gamedata diff found for build version: <strong>{{buildVersionParam}}</strong> ({{envParam}})</p>
    <p *ngIf="buildVersionParam" class="hint">This could mean the diff hasn't been generated yet or the build failed.</p>
    <p *ngIf="!params && !buildVersionParam">No diff ID or build version provided.</p>
    <p class="hint">Please check the URL parameters and try again.</p>
  </div>

  <!-- ===== MAIN CONTENT ===== -->
  <ng-container *ngIf="!isLoading && selectedDiff">

    <!-- Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card stat-blue">
        <div class="stat-card-icon">📊</div>
        <div class="stat-card-body">
          <div class="stat-card-label">TABLES CHANGED</div>
          <div class="stat-card-value">{{statsData.tablesChanged}}</div>
          <div class="stat-card-sub">out of {{selectedDiff?.metadata?.totalTablesCompared || tableOverview.length + '+'}} compared</div>
        </div>
      </div>
      <div class="stat-card stat-green">
        <div class="stat-card-icon">✨</div>
        <div class="stat-card-body">
          <div class="stat-card-label">RECORDS CREATED</div>
          <div class="stat-card-value">{{statsData.recordsCreated}}</div>
          <div class="stat-card-sub" [class.muted]="statsData.recordsCreated === 0">
            {{statsData.recordsCreated === 0 ? 'No additions' : '+' + statsData.recordsCreated + ' vs baseline'}}
          </div>
        </div>
      </div>
      <div class="stat-card stat-orange">
        <div class="stat-card-icon">🔄</div>
        <div class="stat-card-body">
          <div class="stat-card-label">RECORDS UPDATED</div>
          <div class="stat-card-value">{{statsData.recordsUpdated}}</div>
          <div class="stat-card-sub" [class.muted]="statsData.recordsUpdated === 0">
            {{statsData.recordsUpdated === 0 ? 'No updates' : '+' + statsData.recordsUpdated + ' vs baseline'}}
          </div>
        </div>
      </div>
      <div class="stat-card stat-red">
        <div class="stat-card-icon">🗑️</div>
        <div class="stat-card-body">
          <div class="stat-card-label">RECORDS DELETED</div>
          <div class="stat-card-value">{{statsData.recordsDeleted}}</div>
          <div class="stat-card-sub" [class.muted]="statsData.recordsDeleted === 0">
            {{statsData.recordsDeleted === 0 ? 'No deletions' : '-' + statsData.recordsDeleted + ' vs baseline'}}
          </div>
        </div>
      </div>
    </div>

    <!-- AI Analysis Banner -->
    <div class="ai-banner" *ngIf="selectedDiff?.diff?.shortResume || selectedDiff?.diff?.completedResume">
      <div class="ai-banner-icon">🤖</div>
      <div class="ai-banner-body">
        <div class="ai-short" *ngIf="selectedDiff?.diff?.shortResume">{{selectedDiff.diff.shortResume}}</div>
        <div class="ai-detail" *ngIf="selectedDiff?.diff?.completedResume">{{selectedDiff.diff.completedResume}}</div>
      </div>
    </div>
    <div class="ai-pending-banner" *ngIf="!selectedDiff?.diff?.shortResume && !selectedDiff?.diff?.completedResume">
      🤖 AI analysis pending…
    </div>

    <!-- Body: Sidebar + Main + Detail Panel -->
    <div class="diff-body" [class.has-panel]="selectedRecord">

      <!-- ===== LEFT SIDEBAR ===== -->
      <div class="diff-sidebar">
        <div class="sidebar-search-row">
          <input
            class="sidebar-search-input"
            [(ngModel)]="sidebarSearch"
            placeholder="Search tables…"
            autocomplete="off"
          />
          <span class="sidebar-search-icon">🔍</span>
        </div>

        <div class="sidebar-section-label">
          TABLES COMPARED ({{tableOverview.length}})
        </div>

        <div class="sidebar-table-list">
          <div
            *ngFor="let table of filteredTableList; trackBy: trackByTableName"
            class="sidebar-table-item"
            [class.active]="table.name === selectedTableName"
            (click)="selectTable(table.name)"
          >
            <div class="sidebar-item-top">
              <span class="sidebar-table-name">{{table.name | titlecase}}</span>
              <span class="sidebar-table-changes">{{getTotalChanges(table)}} change{{getTotalChanges(table) !== 1 ? 's' : ''}}</span>
            </div>
            <div class="sidebar-item-badges">
              <span class="mini-badge badge-green" title="Added">{{table.createdCount}}</span>
              <span class="mini-badge badge-red" title="Deleted">{{table.deletedCount}}</span>
              <span class="mini-badge badge-orange" title="Updated">{{table.editedCount}}</span>
              <span class="mini-badge badge-gray" title="Cells Changed">{{table.cellsChanged}}</span>
            </div>
          </div>

          <div *ngIf="filteredTableList.length === 0" class="sidebar-empty">
            No tables match "{{sidebarSearch}}"
          </div>
        </div>

        <!-- Legend -->
        <div class="sidebar-legend">
          <div class="legend-item"><span class="legend-dot dot-green"></span> Added</div>
          <div class="legend-item"><span class="legend-dot dot-red"></span> Deleted</div>
          <div class="legend-item"><span class="legend-dot dot-orange"></span> Updated</div>
          <div class="legend-item"><span class="legend-dot dot-gray"></span> Cells Changed</div>
        </div>
      </div>

      <!-- ===== MAIN DIFF AREA ===== -->
      <div class="diff-main">

        <!-- Table title + impact + filter row -->
        <div class="diff-main-header" *ngIf="selectedTableOverview">
          <div class="diff-main-title-row">
            <h3 class="diff-table-title">
              {{selectedTableName | titlecase}}
              <span class="change-count-chip">{{getTotalChanges(selectedTableOverview)}} changes</span>
            </h3>
            <span
              class="impact-badge"
              [class]="getImpactClass(selectedTableOverview)"
              [title]="getImpactReason(selectedTableOverview)"
            >
              {{getImpactClass(selectedTableOverview) === 'impact-high' ? '🔴' : getImpactClass(selectedTableOverview) === 'impact-medium' ? '⚠️' : '🟢'}}
              {{getImpactLabel(selectedTableOverview)}}
            </span>
            <span class="truncated-chip" *ngIf="selectedTableOverview?.isTruncated" title="Only the first 1000 updated rows are shown">⚠️ Truncated</span>
          </div>

          <div class="diff-filter-row">
            <span class="filter-label">Filters:</span>
            <button
              class="filter-chip filter-chip-added"
              [class.filter-active]="isFilterActive('added')"
              (click)="toggleFilter('added')"
              title="Show added records"
            >
              Added
              <span class="filter-chip-count">{{selectedTableOverview?.createdCount || 0}}</span>
            </button>
            <button
              class="filter-chip filter-chip-deleted"
              [class.filter-active]="isFilterActive('deleted')"
              (click)="toggleFilter('deleted')"
              title="Show deleted records"
            >
              Deleted
              <span class="filter-chip-count">{{selectedTableOverview?.deletedCount || 0}}</span>
            </button>
            <button
              class="filter-chip filter-chip-updated"
              [class.filter-active]="isFilterActive('updated')"
              (click)="toggleFilter('updated')"
              title="Show updated records"
            >
              Updated
              <span class="filter-chip-count">{{selectedTableOverview?.editedCount || 0}}</span>
            </button>

            <div class="filter-search-wrapper">
              <span class="filter-search-icon">🔍</span>
              <input
                class="filter-search-input"
                [(ngModel)]="rowSearch"
                (ngModelChange)="onRowSearchChange()"
                [placeholder]="'Search in ' + (selectedTableName | titlecase) + '…'"
                autocomplete="off"
              />
            </div>

            <button class="btn-export-mini" (click)="exportToCSV()" title="Export current diff to CSV">
              📤 Export
            </button>
          </div>
        </div>

        <!-- Empty table state -->
        <div *ngIf="!selectedTableName" class="diff-empty-state">
          <p>Select a table from the sidebar to view changes.</p>
        </div>

        <!-- Diff table -->
        <div class="diff-table-wrapper" *ngIf="selectedTableName">
          <table class="diff-table">
            <thead>
              <tr>
                <th class="col-id">ID</th>
                <th class="col-type">Change Type</th>
                <th class="col-field">Field</th>
                <th class="col-before">Before</th>
                <th class="col-after">After</th>
                <th class="col-delta">Delta</th>
                <th class="col-action"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let row of filteredRows; trackBy: trackByChangeId"
                class="diff-row"
                [class.row-added]="row.changeType === 'added'"
                [class.row-deleted]="row.changeType === 'deleted'"
                [class.row-updated]="row.changeType === 'updated'"
                [class.row-selected]="selectedRecord && selectedRecord.recordId === row.recordId && selectedRecord.fieldName === row.fieldName"
                (click)="selectRecord(row)"
              >
                <td class="col-id">
                  <button
                    class="record-id-btn"
                    (click)="navigateToRecord(selectedTableName, row.recordId); $event.stopPropagation()"
                    [title]="'View ' + selectedTableName + ' record ' + row.recordId"
                  >{{row.recordId}}</button>
                </td>
                <td class="col-type">
                  <span class="change-type-badge" [class]="'badge-' + row.changeType">
                    {{row.changeType | titlecase}}
                  </span>
                </td>
                <td class="col-field">{{row.fieldName}}</td>
                <td class="col-before">
                  <ng-container *ngIf="row.stringDiff; else plainBefore">
                    <span class="str-value" [title]="row.oldValue">
                      <span class="str-context">{{row.stringDiff.prefix}}</span><span class="str-removed">{{row.stringDiff.removed}}</span><span class="str-context">{{row.stringDiff.suffix}}</span>
                    </span>
                  </ng-container>
                  <ng-template #plainBefore>
                    <span class="value-cell value-old" [title]="formatValue(row.oldValue)">{{formatValueShort(row.oldValue)}}</span>
                  </ng-template>
                </td>
                <td class="col-after">
                  <ng-container *ngIf="row.stringDiff; else plainAfter">
                    <span class="str-value" [title]="row.newValue">
                      <span class="str-context">{{row.stringDiff.prefix}}</span><span class="str-added">{{row.stringDiff.added}}</span><span class="str-context">{{row.stringDiff.suffix}}</span>
                    </span>
                  </ng-container>
                  <ng-template #plainAfter>
                    <span class="value-cell value-new" [title]="formatValue(row.newValue)">{{formatValueShort(row.newValue)}}</span>
                  </ng-template>
                </td>
                <td class="col-delta">
                  <ng-container *ngIf="getDeltaInfo(row.oldValue, row.newValue) as di; else noDelta">
                    <div class="delta-cell" [class]="getDeltaClass(di.value)">
                      <span class="delta-abs">{{formatDeltaAbsolute(di.value)}}</span>
                      <span class="delta-pct">{{formatDeltaPercent(di.value, row.oldValue)}}</span>
                    </div>
                  </ng-container>
                  <ng-template #noDelta><span class="delta-na">—</span></ng-template>
                </td>
                <td class="col-action">
                  <span class="row-chevron">›</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Empty filtered state -->
          <div *ngIf="filteredRows.length === 0" class="table-empty">
            <p *ngIf="totalRows === 0 && !rowSearch">No changes match the active filters.</p>
            <p *ngIf="rowSearch">No results for "{{rowSearch}}".</p>
          </div>

          <!-- Table footer / pagination -->
          <div class="table-footer" *ngIf="totalRows > 0">
            <span class="footer-count">
              Showing {{pageStart}} to {{pageEnd}} of {{totalRows}} results
            </span>
            <div class="pagination-controls">
              <button class="page-btn" (click)="prevPage()" [disabled]="currentPage === 1">‹</button>
              <span class="page-indicator">{{currentPage}}</span>
              <button class="page-btn" (click)="nextPage()" [disabled]="currentPage === totalPages">›</button>
            </div>
            <select class="page-size-select" [ngModel]="pageSize" (ngModelChange)="onPageSizeChange($event)">
              <option *ngFor="let s of pageSizeOptions" [value]="s">{{s}} / page</option>
            </select>
          </div>
        </div>
      </div>

      <!-- ===== RIGHT DETAIL PANEL ===== -->
      <div class="diff-detail-panel" *ngIf="selectedRecord">
        <div class="panel-header">
          <div class="panel-header-left">
            <div class="panel-record-label">Record</div>
            <div class="panel-record-id">{{selectedRecord.recordId}}</div>
          </div>
          <button class="panel-close-btn" (click)="closeDetailPanel()" title="Close panel">✕</button>
        </div>

        <div class="panel-meta-row">
          <span class="change-type-badge" [class]="'badge-' + selectedRecord.changeType">
            {{selectedRecord.changeType | titlecase}}
          </span>
          <span class="panel-field-count">
            {{selectedRecordChanges.length}} field{{selectedRecordChanges.length !== 1 ? 's' : ''}} changed
          </span>
        </div>

        <div class="panel-section">
          <div class="panel-section-header">
            Changed Fields
            <span class="panel-section-count">{{selectedRecordChanges.length}}</span>
          </div>

          <div *ngFor="let fc of selectedRecordChanges" class="panel-field-block">
            <div class="panel-field-name">{{fc.fieldName}}</div>

            <!-- Numeric field: show values + prominent delta -->
            <ng-container *ngIf="getDeltaInfo(fc.oldValue, fc.newValue) as di">
              <div class="panel-numeric-comparison">
                <div class="panel-num-block">
                  <span class="panel-num-label">Before</span>
                  <span class="panel-num-value panel-num-old">{{fc.oldValue | number}}</span>
                </div>
                <span class="panel-num-arrow">→</span>
                <div class="panel-num-block">
                  <span class="panel-num-label">After</span>
                  <span class="panel-num-value panel-num-new">{{fc.newValue | number}}</span>
                </div>
              </div>
              <div class="panel-delta-prominent" [class]="getDeltaClass(di.value)">
                <span class="panel-delta-abs">Δ {{formatDeltaAbsolute(di.value)}}</span>
                <span class="panel-delta-pct" *ngIf="formatDeltaPercent(di.value, fc.oldValue)">
                  ({{formatDeltaPercent(di.value, fc.oldValue)}})
                </span>
              </div>
            </ng-container>

            <!-- String field: inline diff highlighting -->
            <ng-container *ngIf="!getDeltaInfo(fc.oldValue, fc.newValue)">
              <ng-container *ngIf="fc.stringDiff; else plainPanelValues">
                <div class="panel-string-diff">
                  <div class="panel-str-row">
                    <span class="panel-str-label">Before</span>
                    <span class="panel-str-value">
                      <span class="str-context">{{fc.stringDiff.prefix}}</span><span class="str-removed">{{fc.stringDiff.removed}}</span><span class="str-context">{{fc.stringDiff.suffix}}</span>
                    </span>
                  </div>
                  <div class="panel-str-row">
                    <span class="panel-str-label">After</span>
                    <span class="panel-str-value">
                      <span class="str-context">{{fc.stringDiff.prefix}}</span><span class="str-added">{{fc.stringDiff.added}}</span><span class="str-context">{{fc.stringDiff.suffix}}</span>
                    </span>
                  </div>
                  <div class="panel-str-note" *ngIf="fc.stringDiffSummary">
                    💬 {{fc.stringDiffSummary}}
                  </div>
                </div>
              </ng-container>

              <ng-template #plainPanelValues>
                <div class="panel-field-comparison">
                  <div class="panel-value-block">
                    <span class="panel-value-label">Before</span>
                    <span class="panel-value panel-value-old" [title]="formatValue(fc.oldValue)">{{formatValue(fc.oldValue)}}</span>
                  </div>
                  <span class="panel-arrow">→</span>
                  <div class="panel-value-block">
                    <span class="panel-value-label">After</span>
                    <span class="panel-value panel-value-new" [title]="formatValue(fc.newValue)">{{formatValue(fc.newValue)}}</span>
                  </div>
                </div>
              </ng-template>
            </ng-container>
          </div>
        </div>

        <div class="panel-section">
          <div class="panel-section-header">Change Details</div>
          <div class="panel-detail-grid">
            <span class="detail-key">Change Type</span>
            <span class="detail-val">
              <span class="change-type-badge" [class]="'badge-' + selectedRecord.changeType">{{selectedRecord.changeType | titlecase}}</span>
            </span>
            <span class="detail-key">Changed At</span>
            <span class="detail-val">{{selectedDiff?.generatedAt | date:'medium'}}</span>
            <span class="detail-key">Source</span>
            <span class="detail-val">Baseline Build</span>
            <span class="detail-key">Target</span>
            <span class="detail-val">Current Build</span>
          </div>
        </div>

        <button
          class="panel-navigate-btn"
          (click)="navigateToRecord(selectedTableName, selectedRecord.recordId)"
        >
          View Full Record ↗
        </button>
      </div>
    </div>

    <!-- ===== DIFF INFORMATION CARD ===== -->
    <div class="diff-info-card">
      <div class="diff-info-card-header">
        <span class="diff-info-card-icon">📋</span>
        <span class="diff-info-card-title">Diff Information</span>
      </div>
      <div class="diff-info-grid">
        <div class="diff-info-item">
          <div class="info-label">Build Version</div>
          <div class="info-value version-value">
            <span class="version-text" [title]="selectedDiff?.buildVersion">
              {{selectedDiff?.buildVersion | slice:0:32}}{{(selectedDiff?.buildVersion?.length || 0) > 32 ? '…' : ''}}
            </span>
            <button class="copy-btn" (click)="copyToClipboard(selectedDiff?.buildVersion)" [title]="copySuccess ? 'Copied!' : 'Copy build version'">
              {{copySuccess ? '✓' : '⎘'}}
            </button>
          </div>
        </div>
        <div class="diff-info-item">
          <div class="info-label">Environment</div>
          <div class="info-value">
            <span class="environment-badge" [class]="'env-' + (selectedDiff?.environment || 'unknown')">
              {{selectedDiff?.environment || 'Unknown'}}
            </span>
          </div>
        </div>
        <div class="diff-info-item">
          <div class="info-label">Generated At</div>
          <div class="info-value">{{selectedDiff?.generatedAt | date:'medium'}}</div>
        </div>
        <div class="diff-info-item">
          <div class="info-label">Created At</div>
          <div class="info-value">{{selectedDiff?.createdAt | date:'medium'}}</div>
        </div>
        <div class="diff-info-item" *ngIf="selectedDiff?.metadata?.processingTimeMs">
          <div class="info-label">Processing Time</div>
          <div class="info-value">⏱ {{selectedDiff.metadata.processingTimeMs}}ms</div>
        </div>
        <div class="diff-info-item" *ngIf="selectedDiff?.metadata?.totalTablesCompared">
          <div class="info-label">Total Tables Compared</div>
          <div class="info-value">{{selectedDiff.metadata.totalTablesCompared}}</div>
        </div>
        <div class="diff-info-item" *ngIf="selectedDiff?.metadata?.totalRowsCompared">
          <div class="info-label">Total Rows Compared</div>
          <div class="info-value">{{selectedDiff.metadata.totalRowsCompared}}</div>
        </div>
      </div>
      <div class="diff-info-timezone-note">⏰ All times shown in local timezone</div>
    </div>

    <!-- ===== RAW DATA (dev) ===== -->
    <div class="raw-data-section">
      <h3 (click)="toggleRawData()" class="raw-data-toggle">
        🔧 Raw Data <span class="raw-data-hint">{{showRawData ? '(Hide)' : '(Show)'}}</span>
      </h3>
      <div *ngIf="showRawData" class="raw-data-body">
        <h4>Selected Diff Full Data:</h4>
        <pre>{{selectedDiff | json}}</pre>
        <h4>Diff Tables Structure:</h4>
        <pre>{{selectedDiff?.diff?.tables | json}}</pre>
      </div>
    </div>

  </ng-container>
</div>
